import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useFetchTemplates from "@pages/Template/utils/useFetchTemplates";
import TemplateCard from "@pages/Template/components/TemplateCard";
import TemplatesTabs from "@pages/Template/components/TemplatesTabs";
import ConfirmDeleteModal from "@shared/components/ConfirmationModal";
import SubTitleHeader from "@shared/components/SubTitleHeader";
import useDeleteTemplate from "@pages/Template/utils/useHandleDelete";
import TemplatesSkeleton from "@pages/Template/components/TemplatesSkeleton";
import TemplateCardSkeleton from "@pages/Template/components/TemplateCardSkeleton";
import { useTemplate } from "@utils/useTemplate";
import { templateTypeMapping } from "./utils/constants";
import NewItemButton from "@shared/components/NewItemButton";
import PageContentFilterDropdown from "@shared/components/PageContentFilterDropdown";
import Pagination from "@shared/components/Pagination";

const TEMPLATES_PER_PAGE = 6;

const TemplatesPage: React.FC = () => {
  const [firstLoad, setFirstLoad] = useState(true);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<"Mine" | "All">("Mine");
  const [dropdownFilter, setDropdownFilter] = useState<string>("Latest");
  const [reportTypeDropdownFilter, setReportTypeDropdownFilter] =
    useState<string>("Select Report Type");
  const [templateTypes, setTemplateTypes] = useState<string[]>([]);

  const { fetchTemplatTypes } = useTemplate();

  // Pagination states (added)
  const [currentPage, setCurrentPage] = useState<number>(1);
  const offset = (currentPage - 1) * TEMPLATES_PER_PAGE;

  const { templates, loading, setTemplates, totalCount, loadTemplates } =
    useFetchTemplates(
      activeTab,
      dropdownFilter,
      reportTypeDropdownFilter,
      offset,
      TEMPLATES_PER_PAGE
    );

  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
  const {
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    selectedTemplateId,
    setSelectedTemplateId,
    handleDelete,
  } = useDeleteTemplate();

  const handleCreateTemplate = () => navigate("/create-template");
  const handleEditClick = (templateId: number) =>
    navigate(`/template/${templateId}/edit`);

  const handleDeleteClick = (templateId: number) => {
    setSelectedTemplateId(templateId);
    setIsDeleteModalOpen(true);
  };

  useEffect(() => {
    let delayTimeout: NodeJS.Timeout;

    if (!loading && firstLoad) {
      delayTimeout = setTimeout(() => setFirstLoad(false), 500);
    }

    return () => clearTimeout(delayTimeout);
  }, [loading, firstLoad]);

  useEffect(() => {
    (async () => {
      try {
        const types: string[] = await fetchTemplatTypes();
        let formattedOptions = types
          .filter((type) => templateTypeMapping[type])
          .map((type) => templateTypeMapping[type]);
        formattedOptions = ["Select Report Type", ...formattedOptions];
        setTemplateTypes(formattedOptions);
      } catch (error) {
        console.error("Failed to fetch template types", error);
      }
    })();
  }, []);

  const totalPages = Math.ceil(totalCount / TEMPLATES_PER_PAGE);

  const confirmDelete = async () => {
    await handleDelete();
    await loadTemplates();
  };

  if (loading && firstLoad) {
    return <TemplatesSkeleton />;
  }

  return (
    <div className="px-8 pb-3 flex-1 flex flex-col">
      <SubTitleHeader
        title={`Reporting Templates`}
        subtitle={`Choose, create or edit an existing template`}
        subtitleStyle="text-gray-400 text-sm 2xl:text-base"
        childrenTitle={
          <TemplatesTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            dropdownFilter={dropdownFilter}
            setDropdownFilter={setDropdownFilter}
            reportTypeDropdownFilter={reportTypeDropdownFilter}
            setReportTypeDropdownFilter={setReportTypeDropdownFilter}
            templateTypes={templateTypes}
          />
        }
        children={
          <>
            <div className="flex space-x-2">
              <div className="flex items-center">
                <PageContentFilterDropdown
                  dropdownFilter={dropdownFilter}
                  setDropdownFilter={setDropdownFilter}
                />
              </div>
              <div className="flex items-center justify-end">
                <PageContentFilterDropdown
                  dropdownFilter={reportTypeDropdownFilter}
                  setDropdownFilter={setReportTypeDropdownFilter}
                  options={templateTypes}
                  label="Filter by:"
                />
              </div>
            </div>
            <NewItemButton onClick={handleCreateTemplate} title="Template" />
          </>
        }
      ></SubTitleHeader>

      <div className="flex-1 flex flex-col pt-8">
        {loading ? (
          <div className="flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 2xl:gap-6">
              {Array.from({ length: 6 }).map((_, idx) => (
                <TemplateCardSkeleton key={idx} />
              ))}
            </div>
          </div>
        ) : templates.length === 0 ? (
          <p className="text-gray-500">No templates found.</p>
        ) : (
          <div className="flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 2xl:gap-6">
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onEdit={handleEditClick}
                  onDelete={handleDeleteClick}
                  onCardClick={handleEditClick}
                  dropdownOpen={dropdownOpen}
                  setDropdownOpen={setDropdownOpen}
                />
              ))}
            </div>
          </div>
        )}

        {/* Pagination UI (Added) */}
        {totalPages > 1 && (
          <>
            <Pagination
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              totalPages={totalPages}
            />
          </>
        )}

        <ConfirmDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={confirmDelete}
          entityNames={
            templates.find((t) => t.id === selectedTemplateId)?.name || ""
          }
        />
      </div>
    </div>
  );
};

export default TemplatesPage;
